/**
 * Timestamps are formatted by slicing the API's own string, never by round-tripping
 * through Date: Date is millisecond-precision and would drop the microseconds the audit
 * log is keyed on.
 */
const TS_RE = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})(?:\.(\d{1,6}))?Z$/;

export function formatTs(ts: string, opts: { micros?: boolean } = {}): string {
  const m = TS_RE.exec(ts);
  if (!m) return ts;
  const [, date, time, frac] = m as unknown as [string, string, string, string | undefined];
  return opts.micros ? `${date} ${time}.${(frac ?? '').padEnd(6, '0')} UTC` : `${date} ${time} UTC`;
}

export const formatDate = (ts: string): string => TS_RE.exec(ts)?.[1] ?? ts;

const NUM = new Intl.NumberFormat('en-GB');
export const formatNumber = (n: number): string => NUM.format(n);

const TENTHS = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/**
 * A duration the API gives in microseconds, in the unit that reads best: whole
 * microseconds under a millisecond (a wrapped key's AES-GCM), tenths of a millisecond
 * under a hundred (a Cloud KMS call), whole milliseconds beyond. 42 → "42 µs",
 * 18342 → "18.3 ms", 1204000 → "1,204 ms". The space is a no-break one, so a narrow
 * column never puts the unit on a line of its own.
 */
export function formatMicros(us: number): string {
  if (us < 1_000) return `${us}\u00a0µs`;
  // Rounded before the test, so 99,950 µs is "100 ms" rather than "100.0 ms".
  const tenths = Math.round(us / 100);
  if (tenths < 1_000) return `${TENTHS.format(tenths / 10)}\u00a0ms`;
  return `${NUM.format(Math.round(us / 1_000))}\u00a0ms`;
}

/*
 * Money, by hand. Intl under en-GB writes US dollars as "US$3.00", and rypt's prices are
 * stated as "$3.00" everywhere else — the API's own description, Stripe's invoices. The
 * grouping and decimal point still come from en-GB, like every other number here.
 */
const CENTS = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MILLICENTS = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 5, maximumFractionDigits: 5 });

/** US cents as dollars: 300 → "$3.00". */
export const formatUsdCents = (cents: number): string => `$${CENTS.format(cents / 100)}`;

/** Millicents (thousandths of a cent) as dollars, to the fifth place: 1 → "$0.00001". */
export const formatUsdMillicents = (millicents: number): string => `$${MILLICENTS.format(millicents / 100_000)}`;

/** First 8 characters of a UUID. Enough to recognise, short enough to scan a column. */
export const shortId = (id: string): string => id.slice(0, 8);

export const formatPercent = (part: number, whole: number): string =>
  whole <= 0 ? '—' : `${Math.min(999, Math.round((part / whole) * 100))}%`;
